import * as THREE from 'three';
import {
    BrepVertex,
    BrepEdge,
    BrepFace,
    BrepLoop,
    BrepShell,
    BrepSolid,
    BrepBody,
    BrepCurve,
    BrepSurface,
    MeshAnalysisResult
} from './FikoBrep';

/**
 * BREP Serileştirme/Deserialize Sistemi
 *
 * BREP topolojisini JSON formatına çevirir ve geri yükler
 * STEP/IGES/FikoWrl formatlarında kullanılmak üzere
 */

export class FikoBrepSerializer {
    /**
     * BREP Body'yi JSON'a çevirir
     */
    public static brepToJson(brep: BrepBody): any {
        return {
            version: '1.0',
            tolerance: brep.tolerance,
            boundingBox: this.box3ToJson(brep.boundingBox),
            stats: brep.stats,

            vertices: this.verticesToJson(brep.vertices),
            edges: this.edgesToJson(brep.edges),
            loops: this.loopsToJson(brep.loops),
            faces: this.facesToJson(brep.faces),
            shells: this.shellsToJson(brep.shells),
            solids: this.solidsToJson(brep.solids),
        };
    }

    /**
     * JSON'dan BREP Body oluşturur
     */
    public static jsonToBrep(json: any): BrepBody {
        const brep: BrepBody = {
            vertices: this.jsonToVertices(json.vertices),
            edges: this.jsonToEdges(json.edges),
            loops: this.jsonToLoops(json.loops),
            faces: this.jsonToFaces(json.faces),
            shells: this.jsonToShells(json.shells),
            solids: this.jsonToSolids(json.solids),
            tolerance: json.tolerance ?? 1e-6,
            boundingBox: this.jsonToBox3(json.boundingBox),
            stats: json.stats ?? {
                vertexCount: 0,
                edgeCount: 0,
                faceCount: 0,
                shellCount: 0,
                solidCount: 0,
                totalVolume: 0,
                totalArea: 0,
            }
        };

        return brep;
    }

    /**
     * MeshAnalysisResult'ı JSON'a çevirir
     */
    public static analysisToJson(analysis: MeshAnalysisResult): any {
        return {
            brep: this.brepToJson(analysis.brep),

            features: {
                planes: analysis.features.planes.map(f => f.id),
                cylinders: analysis.features.cylinders.map(f => f.id),
                spheres: analysis.features.spheres.map(f => f.id),
                cones: analysis.features.cones.map(f => f.id),
                freeform: analysis.features.freeform.map(f => f.id),
            },

            topology: analysis.topology,
            geometry: {
                convexEdges: analysis.geometry.convexEdges.map(e => e.id),
                concaveEdges: analysis.geometry.concaveEdges.map(e => e.id),
                sharpEdges: analysis.geometry.sharpEdges.map(e => e.id),
                smoothEdges: analysis.geometry.smoothEdges.map(e => e.id),
            }
        };
    }

    // ========== VERTEX SERIALIZATION ==========

    private static verticesToJson(vertices: Map<string, BrepVertex>): any[] {
        const result: any[] = [];
        vertices.forEach(v => {
            result.push({
                id: v.id,
                position: [v.position.x, v.position.y, v.position.z],
                tolerance: v.tolerance,
                edges: v.edges,
            });
        });
        return result;
    }

    private static jsonToVertices(json: any[]): Map<string, BrepVertex> {
        const vertices = new Map<string, BrepVertex>();
        json?.forEach((v: any) => {
            vertices.set(v.id, {
                id: v.id,
                position: new THREE.Vector3(v.position[0], v.position[1], v.position[2]),
                tolerance: v.tolerance,
                edges: v.edges,
            });
        });
        return vertices;
    }

    // ========== EDGE SERIALIZATION ==========

    private static edgesToJson(edges: Map<string, BrepEdge>): any[] {
        const result: any[] = [];
        edges.forEach(e => {
            result.push({
                id: e.id,
                startVertex: e.startVertex,
                endVertex: e.endVertex,
                curve: this.curveToJson(e.curve),
                faces: e.faces,
                convex: e.convex,
                angle: e.angle,
                length: e.length,
            });
        });
        return result;
    }

    private static jsonToEdges(json: any[]): Map<string, BrepEdge> {
        const edges = new Map<string, BrepEdge>();
        json?.forEach((e: any) => {
            edges.set(e.id, {
                id: e.id,
                startVertex: e.startVertex,
                endVertex: e.endVertex,
                curve: this.jsonToCurve(e.curve),
                faces: e.faces,
                convex: e.convex,
                angle: e.angle,
                length: e.length,
            });
        });
        return edges;
    }

    // ========== CURVE SERIALIZATION ==========

    private static curveToJson(curve: BrepCurve): any {
        switch (curve.type) {
            case 'line':
                return {
                    type: 'line',
                    start: [curve.start.x, curve.start.y, curve.start.z],
                    end: [curve.end.x, curve.end.y, curve.end.z],
                    length: curve.length,
                };
            case 'arc':
                return {
                    type: 'arc',
                    center: [curve.center.x, curve.center.y, curve.center.z],
                    radius: curve.radius,
                    startAngle: curve.startAngle,
                    endAngle: curve.endAngle,
                    normal: [curve.normal.x, curve.normal.y, curve.normal.z],
                    length: curve.length,
                };
            case 'circle':
                return {
                    type: 'circle',
                    center: [curve.center.x, curve.center.y, curve.center.z],
                    radius: curve.radius,
                    normal: [curve.normal.x, curve.normal.y, curve.normal.z],
                    length: curve.length,
                };
            case 'spline':
                return {
                    type: 'spline',
                    controlPoints: curve.controlPoints.map(p => [p.x, p.y, p.z]),
                    knots: curve.knots,
                    degree: curve.degree,
                    length: curve.length,
                };
            default:
                return { type: curve.type };
        }
    }

    private static jsonToCurve(json: any): BrepCurve {
        switch (json.type) {
            case 'line':
                return {
                    type: 'line',
                    start: new THREE.Vector3(json.start[0], json.start[1], json.start[2]),
                    end: new THREE.Vector3(json.end[0], json.end[1], json.end[2]),
                    length: json.length,
                };
            case 'arc':
                return {
                    type: 'arc',
                    center: new THREE.Vector3(json.center[0], json.center[1], json.center[2]),
                    radius: json.radius,
                    startAngle: json.startAngle,
                    endAngle: json.endAngle,
                    normal: new THREE.Vector3(json.normal[0], json.normal[1], json.normal[2]),
                    length: json.length,
                };
            default:
                return {
                    type: 'line',
                    start: new THREE.Vector3(),
                    end: new THREE.Vector3(),
                    length: 0,
                };
        }
    }

    // ========== LOOP SERIALIZATION ==========

    private static loopsToJson(loops: Map<string, BrepLoop>): any[] {
        const result: any[] = [];
        loops.forEach(l => {
            result.push({
                id: l.id,
                edges: l.edges,
                isOuter: l.isOuter,
                vertices: l.vertices,
            });
        });
        return result;
    }

    private static jsonToLoops(json: any[]): Map<string, BrepLoop> {
        const loops = new Map<string, BrepLoop>();
        json?.forEach((l: any) => {
            loops.set(l.id, {
                id: l.id,
                edges: l.edges,
                isOuter: l.isOuter,
                vertices: l.vertices,
            });
        });
        return loops;
    }

    // ========== FACE SERIALIZATION ==========

    private static facesToJson(faces: Map<string, BrepFace>): any[] {
        const result: any[] = [];
        faces.forEach(f => {
            result.push({
                id: f.id,
                surface: this.surfaceToJson(f.surface),
                loops: f.loops.map(l => l.id),
                edges: f.edges,
                normal: [f.normal.x, f.normal.y, f.normal.z],
                area: f.area,
                centroid: [f.centroid.x, f.centroid.y, f.centroid.z],
                color: f.color ? [f.color.r, f.color.g, f.color.b] : null,
            });
        });
        return result;
    }

    private static jsonToFaces(json: any[]): Map<string, BrepFace> {
        const faces = new Map<string, BrepFace>();
        json?.forEach((f: any) => {
            faces.set(f.id, {
                id: f.id,
                surface: this.jsonToSurface(f.surface),
                loops: [], // Loop referansları sonra resolve edilecek
                edges: f.edges,
                normal: new THREE.Vector3(f.normal[0], f.normal[1], f.normal[2]),
                area: f.area,
                centroid: new THREE.Vector3(f.centroid[0], f.centroid[1], f.centroid[2]),
                color: f.color ? new THREE.Color(f.color[0], f.color[1], f.color[2]) : undefined,
            });
        });
        return faces;
    }

    // ========== SURFACE SERIALIZATION ==========

    private static surfaceToJson(surface: BrepSurface): any {
        switch (surface.type) {
            case 'plane':
                return {
                    type: 'plane',
                    origin: [surface.origin.x, surface.origin.y, surface.origin.z],
                    normal: [surface.normal.x, surface.normal.y, surface.normal.z],
                    uAxis: [surface.uAxis.x, surface.uAxis.y, surface.uAxis.z],
                    vAxis: [surface.vAxis.x, surface.vAxis.y, surface.vAxis.z],
                };
            case 'cylinder':
                return {
                    type: 'cylinder',
                    axis: [surface.axis.x, surface.axis.y, surface.axis.z],
                    radius: surface.radius,
                    center: [surface.center.x, surface.center.y, surface.center.z],
                    height: surface.height,
                };
            case 'sphere':
                return {
                    type: 'sphere',
                    center: [surface.center.x, surface.center.y, surface.center.z],
                    radius: surface.radius,
                };
            default:
                return { type: surface.type };
        }
    }

    private static jsonToSurface(json: any): BrepSurface {
        switch (json.type) {
            case 'plane':
                return {
                    type: 'plane',
                    origin: new THREE.Vector3(json.origin[0], json.origin[1], json.origin[2]),
                    normal: new THREE.Vector3(json.normal[0], json.normal[1], json.normal[2]),
                    uAxis: new THREE.Vector3(json.uAxis[0], json.uAxis[1], json.uAxis[2]),
                    vAxis: new THREE.Vector3(json.vAxis[0], json.vAxis[1], json.vAxis[2]),
                };
            case 'cylinder':
                return {
                    type: 'cylinder',
                    axis: new THREE.Vector3(json.axis[0], json.axis[1], json.axis[2]),
                    radius: json.radius,
                    center: new THREE.Vector3(json.center[0], json.center[1], json.center[2]),
                    height: json.height,
                };
            default:
                return {
                    type: 'freeform',
                    triangles: [],
                };
        }
    }

    // ========== SHELL SERIALIZATION ==========

    private static shellsToJson(shells: Map<string, BrepShell>): any[] {
        const result: any[] = [];
        shells.forEach(s => {
            result.push({
                id: s.id,
                faces: s.faces,
                isClosed: s.isClosed,
                volume: s.volume,
                boundingBox: this.box3ToJson(s.boundingBox),
            });
        });
        return result;
    }

    private static jsonToShells(json: any[]): Map<string, BrepShell> {
        const shells = new Map<string, BrepShell>();
        json?.forEach((s: any) => {
            shells.set(s.id, {
                id: s.id,
                faces: s.faces,
                isClosed: s.isClosed,
                volume: s.volume,
                boundingBox: this.jsonToBox3(s.boundingBox),
            });
        });
        return shells;
    }

    // ========== SOLID SERIALIZATION ==========

    private static solidsToJson(solids: Map<string, BrepSolid>): any[] {
        const result: any[] = [];
        solids.forEach(s => {
            result.push({
                id: s.id,
                outerShell: s.outerShell,
                voids: s.voids,
                volume: s.volume,
                centerOfMass: [s.centerOfMass.x, s.centerOfMass.y, s.centerOfMass.z],
                boundingBox: this.box3ToJson(s.boundingBox),
            });
        });
        return result;
    }

    private static jsonToSolids(json: any[]): Map<string, BrepSolid> {
        const solids = new Map<string, BrepSolid>();
        json?.forEach((s: any) => {
            solids.set(s.id, {
                id: s.id,
                outerShell: s.outerShell,
                voids: s.voids,
                volume: s.volume,
                centerOfMass: new THREE.Vector3(s.centerOfMass[0], s.centerOfMass[1], s.centerOfMass[2]),
                boundingBox: this.jsonToBox3(s.boundingBox),
            });
        });
        return solids;
    }

    // ========== HELPER METHODS ==========

    private static box3ToJson(box: THREE.Box3): any {
        return {
            min: [box.min.x, box.min.y, box.min.z],
            max: [box.max.x, box.max.y, box.max.z],
        };
    }

    private static jsonToBox3(json: any): THREE.Box3 {
        if (!json) return new THREE.Box3();
        return new THREE.Box3(
            new THREE.Vector3(json.min[0], json.min[1], json.min[2]),
            new THREE.Vector3(json.max[0], json.max[1], json.max[2])
        );
    }
}
